/**
 * Two-finger zoom and pan for the drawing surfaces.
 *
 * Every 2D drawing view carries its own view model — the plan editor works in
 * world millimetres, the scene views in SVG view-box units — so this hook does
 * not own a transform. It resolves raw pointer events into a single geometric
 * update (how much to zoom, about which point, and how far the fingers
 * travelled) and lets each surface apply that to whatever transform it keeps.
 *
 * The gesture model matches the 3D viewport, which already behaves this way:
 * one finger uses the active tool, two fingers zoom and pan the drawing.
 */

import { useCallback, useRef, useState } from 'react';

export interface GesturePoint {
  x: number;
  y: number;
}

export interface PinchUpdate {
  /**
   * Midpoint between the fingers, in pixels relative to the gesture element.
   * The world point under it must not move while the gesture zooms, or the
   * drawing slides away from the fingers holding it.
   */
  centre: GesturePoint;
  /** Multiplicative zoom since the previous update. */
  scaleFactor: number;
  /** Midpoint travel in pixels since the previous update. */
  panX: number;
  panY: number;
}

interface PointerEventLike {
  pointerId: number;
  clientX: number;
  clientY: number;
  currentTarget: Element;
}

/**
 * Tracks active pointers and reports two-finger updates.
 *
 * The returned `down`/`move`/`up` are called first inside a surface's own
 * pointer handlers. When one returns true the gesture has claimed the event
 * and the surface must not also run its single-pointer behaviour.
 */
export function usePinchGesture({
  onPinch,
  onGestureStart,
}: {
  onPinch: (update: PinchUpdate) => void;
  /**
   * Fired when a second finger lands. The surface cancels whatever the first
   * finger had started, so a zoom never leaves a half-drawn point behind.
   */
  onGestureStart?: () => void;
}) {
  const pointers = useRef(new Map<number, GesturePoint>());
  const previous = useRef<{ centre: GesturePoint; distance: number }>();
  // Once two fingers have been down, the finger left behind when one lifts
  // must not resume drawing or dragging: the user is ending a zoom, not
  // starting a stroke. This stays set until every finger has lifted.
  const claimed = useRef(false);
  const [gesturing, setGesturing] = useState(false);

  const down = useCallback((event: PointerEventLike): boolean => {
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (pointers.current.size < 2) return false;

    if (!claimed.current) {
      claimed.current = true;
      setGesturing(true);
      onGestureStart?.();
    }
    previous.current = undefined;
    return true;
  }, [onGestureStart]);

  const move = useCallback((event: PointerEventLike): boolean => {
    if (!pointers.current.has(event.pointerId)) return false;
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (pointers.current.size < 2) return claimed.current;

    const [first, second] = [...pointers.current.values()];
    if (!first || !second) return claimed.current;
    const rect = event.currentTarget.getBoundingClientRect();
    const centre = {
      x: (first.x + second.x) / 2 - rect.left,
      y: (first.y + second.y) / 2 - rect.top,
    };
    const distance = Math.hypot(second.x - first.x, second.y - first.y);

    const last = previous.current;
    previous.current = { centre, distance };
    // The first update after a finger lands only establishes the reference:
    // there is no previous frame to measure movement against yet.
    if (!last || last.distance <= 0 || distance <= 0) return true;

    onPinch({
      centre,
      scaleFactor: distance / last.distance,
      panX: centre.x - last.centre.x,
      panY: centre.y - last.centre.y,
    });
    return true;
  }, [onPinch]);

  const up = useCallback((event: PointerEventLike): boolean => {
    pointers.current.delete(event.pointerId);
    const wasClaimed = claimed.current;
    if (pointers.current.size < 2) previous.current = undefined;
    if (pointers.current.size === 0 && claimed.current) {
      claimed.current = false;
      setGesturing(false);
    }
    return wasClaimed;
  }, []);

  return { gesturing, down, move, up };
}

/**
 * Applies a pinch update to an SVG view box.
 *
 * Zoom is resolved about the gesture centre so the drawing stays anchored to
 * the fingers, then the whole box follows their travel.
 */
export function viewBoxAfterPinch(
  viewBox: { x: number; y: number; width: number; height: number },
  update: PinchUpdate,
  elementSize: { width: number; height: number },
): { x: number; y: number; width: number; height: number } {
  if (elementSize.width <= 0 || elementSize.height <= 0) return viewBox;

  const unitsPerPixelX = viewBox.width / elementSize.width;
  const unitsPerPixelY = viewBox.height / elementSize.height;
  const anchorX = viewBox.x + update.centre.x * unitsPerPixelX;
  const anchorY = viewBox.y + update.centre.y * unitsPerPixelY;

  const width = viewBox.width / update.scaleFactor;
  const height = viewBox.height / update.scaleFactor;
  // Keep the anchor at the same fraction across the box, then translate by the
  // fingers' travel expressed in the new zoom level's units.
  const fractionX = update.centre.x / elementSize.width;
  const fractionY = update.centre.y / elementSize.height;

  return {
    x: anchorX - width * fractionX - update.panX * (width / elementSize.width),
    y: anchorY - height * fractionY - update.panY * (height / elementSize.height),
    width,
    height,
  };
}
